// ---------------------------------------------------------------------------
// country.zh.ts — Chinese translations for all 5 country landing pages
// ---------------------------------------------------------------------------
import type { CountryText } from './country.content';

export const COUNTRY_ZH: Record<string, CountryText> = {
  us: {
    countryName: '美国',
    heroTitle: '美国酒店管理软件',
    heroSubtitle: '为美国酒店、度假村和酒店集团打造的企业级PMS。集成美国税务合规、PCI DSS支付处理和ADA无障碍数字体验的多物业运营管理。',
    statsLabels: ['全国酒店数', '年行业收入', '平均入住率', '平均日房价 (ADR)'],
    features: [
      { title: '美国税务引擎', description: '自动计算全50个州的临时住宿税（TOT）、州销售税和旅游改善区费用。' },
      { title: 'PCI DSS合规支付', description: 'PCI DSS一级认证支付处理，含令牌化和端到端加密。' },
      { title: 'OTA渠道管理器', description: '与Booking.com、Expedia、Airbnb、VRBO等200+分销渠道实时同步。' },
      { title: 'ADA无障碍数字体验', description: '符合WCAG 2.1 AA标准的预订引擎和客人门户，满足ADA无障碍要求。' },
      { title: 'STR基准对标集成', description: '自动与竞争群组进行RevPAR、ADR和入住率基准对标。' },
      { title: '多物业组合', description: '酒店集团集中管理仪表板，含合并损益表、价格管理和员工排班。' },
    ],
    complianceTitle: '内置美国监管合规',
    complianceItems: [
      { name: '州和地方税', description: '所有辖区的TOT、销售税和TID费用自动计算' },
      { name: 'PCI DSS一级', description: '含令牌化和季度扫描的认证支付卡安全' },
      { name: 'ADA合规', description: '预订和客人服务的数字无障碍标准' },
      { name: 'CCPA / 州隐私', description: '加州消费者隐私法和州级数据保护' },
      { name: 'OSHA工作场所安全', description: '酒店员工安全跟踪和事件报告' },
      { name: '1099-K表格报告', description: 'IRS合规的OTA收入自动报告' },
    ],
    testimonial: { quote: 'HotelStack改变了我们在佛罗里达和加利福尼亚的12家物业组合。仅TOT自动申报就每月节省40小时。多物业仪表板提供了前所未有的实时可见性。', role: '运营副总裁', hotel: 'Coastal Hospitality Group，迈阿密' },
    faqItems: [
      { question: 'HotelStack能处理美国州和地方酒店税吗？', answer: '能。HotelStack自动计算全50个州和主要市政区的TOT、州销售税和TID费用。' },
      { question: 'HotelStack符合PCI DSS吗？', answer: 'HotelStack通过PCI DSS一级认证。所有支付数据经过令牌化和端到端加密。' },
      { question: '能从一个仪表板管理多个美国物业吗？', answer: '当然。我们的多物业模块提供合并报告、集中价格管理和跨物业客人档案。' },
      { question: 'HotelStack与美国OTA集成吗？', answer: '是的。我们连接Booking.com、Expedia、Airbnb、VRBO、Hotels.com等200+渠道。' },
    ],
    ctaTitle: '准备好升级您的美国酒店运营了吗？',
    ctaSubtitle: '加入数百家使用HotelStack优化运营、最大化收入和提供卓越客人体验的美国酒店。',
  },
  uk: {
    countryName: '英国',
    heroTitle: '英国酒店管理软件',
    heroSubtitle: '为英国酒店、精品酒店和连锁酒店打造的企业级PMS。HMRC合规增值税自动化、Making Tax Digital就绪和GDPR优先数据处理。',
    statsLabels: ['酒店和民宿', '年行业收入', '平均入住率', '平均日房价 (ADR)'],
    features: [
      { title: 'HMRC增值税自动化', description: 'Making Tax Digital（MTD）合规的20%增值税申报，自动生成和提交申报表。' },
      { title: 'GDPR优先数据处理', description: '含同意管理、删除权工作流和DPO工具的客人数据处理。' },
      { title: '英国OTA分销', description: '与Booking.com、Expedia、LateRooms和Airbnb实时连接，覆盖英国市场。' },
      { title: 'AA/Visit England集成', description: '与AA星级评定和Visit England认证对齐的质量评估跟踪。' },
      { title: '收入管理', description: '针对英国季节模式和活动优化的需求预测动态定价引擎。' },
      { title: '员工排班（英国劳动法）', description: '符合英国工作时间法规和国家最低工资的排班管理。' },
    ],
    complianceTitle: '内置英国监管合规',
    complianceItems: [
      { name: 'HMRC增值税 / MTD', description: 'Making Tax Digital合规增值税申报，含数字记录保存' },
      { name: 'GDPR', description: '客人数据的完整通用数据保护条例合规' },
      { name: '消防安全令', description: '2005年监管改革（消防安全）令跟踪和文档' },
      { name: '食品卫生（FSA）', description: 'FSA评级合规和HACCP文档' },
      { name: '2010年平等法', description: '残障客人和员工的无障碍要求' },
      { name: '工作权检查', description: '员工验证的移民法合规' },
    ],
    testimonial: { quote: '我们为科茨沃尔德的精品酒店集团切换到了HotelStack。MTD合规的增值税模块和GDPR工具正是我们所需要的。客人满意度分数提高了18%。', role: '总经理', hotel: 'Cotswold Heritage Hotels，牛津郡' },
    faqItems: [
      { question: 'HotelStack符合Making Tax Digital（MTD）吗？', answer: '是的。HotelStack生成MTD合规增值税申报表，含数字记录保存。可直接向HMRC提交。' },
      { question: 'HotelStack如何处理英国酒店的GDPR？', answer: 'HotelStack包含同意管理、自动数据保留策略、删除权工作流和DPO仪表板工��。' },
      { question: 'HotelStack支持英国特定的OTA渠道吗？', answer: '是的。我们与所有英国主要渠道集成，包括Booking.com、Expedia、Airbnb、LateRooms和Trivago。' },
      { question: 'HotelStack能管理英国旅游季节定价吗？', answer: '我们的收入管理引擎支持英国季节需求模式、银行假日定价和活动附加费。' },
    ],
    ctaTitle: '准备好转变您的英国酒店运营了吗？',
    ctaSubtitle: '加入使用HotelStack自动化合规、提升直接预订和提供卓越客人体验的英国领先酒店。',
  },
  uae: {
    countryName: '阿联酋',
    heroTitle: '阿联酋酒店管理软件',
    heroSubtitle: '为阿联酋豪华酒店、度假村和酒店集团打造的企业级PMS。DTCM合规运营、FTA增值税自动化和多语言客人体验。',
    statsLabels: ['7个酋长国的酒店', '年旅游收入', '平均入住率', '平均日房价 (ADR)'],
    features: [
      { title: 'FTA增值税自动化', description: '住宿和餐饮的5%增值税自动计算、申报和FTA税务申报表提交。' },
      { title: 'DTCM合规', description: '旅游迪拉姆征收、分类报告和DTCM许可证管理。' },
      { title: '阿拉伯语+英语界面', description: '完整的RTL/LTR双语界面，含阿拉伯语客人通信和发票。' },
      { title: 'eChannels集成', description: '自动向警察eChannels系统提交客人数据，满足移民合规。' },
      { title: 'WPS薪资集成', description: '符合阿联酋工资保护系统要求的员工薪资处理。' },
      { title: '奢华收入优化', description: 'AI驱动的高端细分动态定价，含团体和MICE价格��理。' },
    ],
    complianceTitle: '阿联酋监管合规自动化',
    complianceItems: [
      { name: 'FTA增值税申报', description: '5%增值税自动计算和FTA申报表提交' },
      { name: '旅游迪拉姆', description: '每晚旅游费征收和DTCM缴纳' },
      { name: '市政费', description: '客房收入的酋长国特定市政费' },
      { name: 'eChannels客人报告', description: '自动向警察系统提交客人数据' },
      { name: 'DTCM酒店许可证', description: '分类和营业许可证合规跟踪' },
      { name: 'PDPL数据保护', description: '客人记录的阿联酋个人数据保护法合规' },
    ],
    testimonial: { quote: 'HotelStack无缝管理我们五星级度假村的运营。DTCM集成和旅游迪拉姆自动化完全消除了手动报告。我们的迪拜团队每天处理300+次入住。', role: '总经理', hotel: 'Palm Residences Resort & Spa，迪拜' },
    faqItems: [
      { question: 'HotelStack处理阿联酋增值税和旅游迪拉姆费用吗？', answer: '是的。HotelStack自动计算5%增值税并征收旅游迪拉姆费用（每房每晚7-20 AED）。' },
      { question: 'HotelStack能向阿联酋警察eChannels提交客人数据吗？', answer: 'HotelStack与警察eChannels系统集成，实现自动客人登记。' },
      { question: 'HotelStack支持阿拉伯语吗？', answer: '是的。HotelStack提供完整的双语界面，含阿拉伯语RTL支持。' },
      { question: 'HotelStack适合阿联酋豪华酒店吗？', answer: '当然。HotelStack服务于五星级酒店和度假村，具备MICE管理和水疗等高级功能。' },
    ],
    ctaTitle: '提升您的阿联酋酒店运营',
    ctaSubtitle: '加入使用HotelStack自动化合规、优化收入和提供世界级客人体验的阿联酋高端酒店。',
  },
  thailand: {
    countryName: '泰国',
    heroTitle: '泰国酒店管理软件',
    heroSubtitle: '为泰国酒店、度假村和酒店企业打造的企业级PMS。TM.30移民合规、泰国税务局集成和多语言支持。',
    statsLabels: ['酒店和度假村', '旅游GDP贡献', '平均入住率', '平均日房价 (ADR)'],
    features: [
      { title: 'TM.30自动提交', description: '入住后24小时内自动向移民局提交外国客人TM.30通知。' },
      { title: '泰国增值税引擎', description: '7%增值税计算，含承包商付款预扣税和税务局申报���' },
      { title: '泰语+英语+中文', description: '支持泰语、英语和简体中文的三语界面，服务员工和客人。' },
      { title: '酒店法（Ror Ror 1）', description: '泰国酒店法要求的许可证跟踪和合规文档。' },
      { title: '旅游局集成', description: 'TAT注册、SHA Plus认证跟踪和旅游统计报告。' },
      { title: '季节性收入管理', description: '针对泰国旺/淡季优化的动态定价，含节日和活动附加费。' },
    ],
    complianceTitle: '泰国监管合规自动化',
    complianceItems: [
      { name: 'TM.30移民', description: '到达后24小时内自动外国人通知' },
      { name: '税务局增值税', description: '7%增值税申报和预扣税合规' },
      { name: '酒店法许可证', description: 'Ror Ror 1酒店营业许可证合规跟踪' },
      { name: 'PDPA数据保护', description: '客人数据的泰国PDPA合规' },
      { name: '消费税许可证', description: '酒类和娱乐许可证管理（Por Sor 1）' },
      { name: 'FDA食品许可证', description: '酒店餐厅Sor Bor 1食品机构许可证' },
    ],
    testimonial: { quote: '仅TM.30自动化就证明了我们切换到HotelStack的价值。我们在普吉岛每周处理200+外国客人入住，移民合规完全自动化。动态定价引擎使收入增长22%。', role: '运营��监', hotel: 'Andaman Bay Resorts，普吉岛' },
    faqItems: [
      { question: 'HotelStack自动化TM.30外国人通知吗？', answer: '是的。HotelStack在外国客人入住后24小时内自动生成并提交TM.30通知至移民局。' },
      { question: 'HotelStack处理泰国增值税和预扣税吗？', answer: 'HotelStack计算住宿和餐饮的7%增值税，管理预扣税并生成税务申报表。' },
      { question: 'HotelStack有泰语版本吗？', answer: '是的。HotelStack支持泰语、英语和中文界面，用于运营、通信、发票和报��。' },
      { question: 'HotelStack能管理泰国的度假村运营吗？', answer: '当然。HotelStack支持水疗管理、泳池/海滩服务、短途旅行预订和多出口餐饮运营。' },
    ],
    ctaTitle: '升级您的泰国酒店运营',
    ctaSubtitle: '加入使用HotelStack自动化合规、提升收入和创造难忘客人体验的泰国领先酒店和度假村。',
  },
  australia: {
    countryName: '澳大利亚',
    heroTitle: '澳大利亚酒店管���软件',
    heroSubtitle: '为澳大利亚酒店、度假村和住宿提供商打造的企业级PMS。ATO合规GST自动化、BAS申报和退休金管理。',
    statsLabels: ['酒店和住宿', '年旅游收入', '平均入住率', '平均日房价 (ADR)'],
    features: [
      { title: 'ATO GST和BAS申报', description: '10% GST自动计算，含季度Business Activity Statement生成和ATO提交。' },
      { title: '退休金管理', description: '11.5% Super Guarantee自动缴款，含SuperStream合规支付。' },
      { title: '隐私法合规', description: '澳大利亚隐私原则（APPs）内置于客人数据收集、存储和处理中。' },
      { title: 'AU/NZ OTA分销', description: '与Booking.com、Expedia、Airbnb、Stayz和Wotif连接，覆盖澳大利亚市场。' },
      { title: '偏远物业支持', description: '为偏远地区住宿提供商提供低带宽模式和离线功能。' },
      { title: 'Tourism Australia集成', description: '星级评定对齐、质量认证跟踪和ABS旅游统计报告。' },
    ],
    complianceTitle: '内置澳大利亚监管合规',
    complianceItems: [
      { name: 'GST / BAS申报', description: '10% GST自动计算和季度Business Activity Statements' },
      { name: 'PAYG预扣', description: '员工税款预扣和ATO自动缴纳' },
      { name: 'Super Guarantee', description: 'SuperStream合规的11.5%退休金缴款' },
      { name: '隐私法（APPs）', description: '客人数据保护的澳大利亚隐私原则' },
      { name: 'WHS合规', description: '酒店员工工作健康安全法跟踪' },
      { name: '酒类许可证', description: '州特定酒类许可证跟踪和续期提醒' },
    ],
    testimonial: { quote: 'HotelStack完美处理我们的BAS申报和退休金缴款。我们从悉尼到凯恩斯运营8家物业，多物业仪表板提供即时可见性。员工喜欢直观的界面。', role: '首席运营官', hotel: 'Pacific Coast Hotels，悉尼' },
    faqItems: [
      { question: 'HotelStack处理澳大利亚GST和BAS申报吗？', answer: '是的。HotelStack自动计算10% GST，生成季度BAS并支持ATO直接提交。' },
      { question: 'HotelStack能管理酒店员工退休金吗？', answer: 'HotelStack计算和跟踪11.5% Super Guarantee缴款，生成SuperStream合规支付文件。' },
      { question: 'HotelStack与澳大利亚OTA集成吗？', answer: '是的。我们连接Booking.com、Expedia、Airbnb、Stayz、Wotif等200+渠道。' },
      { question: 'HotelStack适合澳大利亚偏远地区物业吗？', answer: '当然。HotelStack包含低带宽模式和离线功能，恢复连接时同步数据。' },
    ],
    ctaTitle: '转变您的澳大利亚酒店运营',
    ctaSubtitle: '加入使用HotelStack自动化合规、优化收入和在每个物业提供卓越客人体验的澳大利亚领先酒店。',
  },
};
